import { FilterType } from "@/lib/filters";
import { cn } from "@/lib/utils";
import {
  Equal,
  EqualNot,
  Minus,
  Plus,
  SearchCheck,
  SearchX,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PiGreaterThan,
  PiGreaterThanOrEqual,
  PiLessThan,
  PiLessThanOrEqual,
} from "react-icons/pi";

interface FilterTypeOptionsProps {
  filterType: FilterType;
  onChange: (filterType: FilterType) => void;
  className?: string;
}

interface FilterOption {
  value: FilterType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const FILTER_TYPE_ICONS: Record<
  FilterType,
  React.ComponentType<{ className?: string }>
> = {
  [FilterType.IncludesString]: SearchCheck,
  [FilterType.ExcludesString]: SearchX,
  [FilterType.EqualsString]: Equal,
  [FilterType.NotEquals]: EqualNot,
  [FilterType.LessThan]: PiLessThan,
  [FilterType.GreaterThan]: PiGreaterThan,
  [FilterType.LessThanOrEqual]: PiLessThanOrEqual,
  [FilterType.GreaterThanOrEqual]: PiGreaterThanOrEqual,
  [FilterType.IsEmpty]: Minus,
  [FilterType.IsNotEmpty]: Plus,
};

interface FilterSeparator {
  separator: true;
}

type FilterOptionItem = FilterOption | FilterSeparator;

function isSeparator(item: FilterOptionItem): item is FilterSeparator {
  return "separator" in item && item.separator === true;
}

export function FilterTypeOptions({
  filterType,
  onChange,
  className,
}: FilterTypeOptionsProps) {
  const { t } = useTranslation();

  const filterOptions = useMemo(
    (): FilterOptionItem[] => [
      {
        value: FilterType.IncludesString,
        label: t("table.filters.includesString"),
        icon: SearchCheck,
      },
      {
        value: FilterType.ExcludesString,
        label: t("table.filters.excludesString"),
        icon: SearchX,
      },
      {
        separator: true,
      },
      {
        value: FilterType.EqualsString,
        label: t("table.filters.equalsString"),
        icon: Equal,
      },
      {
        value: FilterType.NotEquals,
        label: t("table.filters.notEquals"),
        icon: EqualNot,
      },
      {
        value: FilterType.LessThan,
        label: t("table.filters.lessThan"),
        icon: PiLessThan,
      },
      {
        value: FilterType.GreaterThan,
        label: t("table.filters.greaterThan"),
        icon: PiGreaterThan,
      },
      {
        value: FilterType.LessThanOrEqual,
        label: t("table.filters.lessThanOrEqual"),
        icon: PiLessThanOrEqual,
      },
      {
        value: FilterType.GreaterThanOrEqual,
        label: t("table.filters.greaterThanOrEqual"),
        icon: PiGreaterThanOrEqual,
      },
      {
        separator: true,
      },
      {
        value: FilterType.IsEmpty,
        label: t("table.filters.isEmpty"),
        icon: Minus,
      },
      {
        value: FilterType.IsNotEmpty,
        label: t("table.filters.isNotEmpty"),
        icon: Plus,
      },
    ],
    [t],
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {filterOptions.map((option, index) =>
        isSeparator(option) ? (
          <div key={index} className="h-px bg-border my-1" />
        ) : (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              "flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground transition-colors",
              filterType === option.value && "bg-accent text-accent-foreground",
            )}
          >
            <option.icon className="h-4 w-4" />
            {option.label}
          </button>
        ),
      )}
    </div>
  );
}
